import React, { useState, useCallback, useEffect } from "react";
import { Box, Typography, Button, CircularProgress, IconButton, Chip, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Select, FormControl, InputLabel, Switch, FormControlLabel, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from "@mui/material";
import { Plus, Edit2, Trash2 } from "lucide-react";
import { getWebsitePaymentMethods, createWebsitePaymentMethod, updateWebsitePaymentMethod, deleteWebsitePaymentMethod } from "../../../api/index";
import { useTranslation } from "react-i18next";
const EMPTY = {
  name: "",
  type: "cash",
  details: "",
  isEnabled: true
};
const TYPE_LABELS = {
  bank_transfer: "Bank Transfer",
  cash: "Cash",
  custom: "Custom"
};
function usePaymentMethods() {
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getWebsitePaymentMethods();
      setMethods(res.data?.data ?? res.data ?? []);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    reload();
  }, [reload]);
  return {
    methods,
    loading,
    reload
  };
}
const PaymentMethodsTab = () => {
  const {
    t
  } = useTranslation("website");
  const {
    methods,
    loading,
    reload
  } = usePaymentMethods();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };
  const openEdit = m => {
    setEditing(m);
    setForm({
      name: m.name,
      type: m.type,
      details: m.details || "",
      isEnabled: m.isEnabled
    });
    setOpen(true);
  };
  const save = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (editing) await updateWebsitePaymentMethod(editing._id, form);else await createWebsitePaymentMethod(form);
      setOpen(false);
      reload();
    } catch (e) {
      alert(e.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };
  const remove = async id => {
    if (!window.confirm(t("Delete this payment method?"))) return;
    await deleteWebsitePaymentMethod(id);
    reload();
  };
  if (loading) return <Box sx={{
    textAlign: "center",
    py: 6
  }}><CircularProgress /></Box>;
  return <Box>
      <Box sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      mb: 3
    }}>
        <Typography variant="h6">{t("Payment Methods")}</Typography>
        <Button variant="contained" startIcon={<Plus size={16} />} onClick={openNew}>{t("Add Method")}</Button>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{
            bgcolor: "grey.50"
          }}>
              <TableCell sx={{
              fontWeight: 600
            }}>{t("Name")}</TableCell>
              <TableCell sx={{
              fontWeight: 600
            }}>{t("Type")}</TableCell>
              <TableCell sx={{
              fontWeight: 600
            }}>{t("Details")}</TableCell>
              <TableCell sx={{
              fontWeight: 600
            }}>{t("Status")}</TableCell>
              <TableCell sx={{
              fontWeight: 600
            }} align="right">{t("Actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {methods.length === 0 && <TableRow>
                <TableCell colSpan={5} align="center" sx={{
              color: "text.secondary",
              py: 4
            }}>{t("No payment methods added yet")}

              </TableCell>
              </TableRow>}
            {methods.map(m => <TableRow key={m._id} hover>
                <TableCell><Typography variant="body2" fontWeight={600}>{m.name}</Typography></TableCell>
                <TableCell><Chip label={TYPE_LABELS[m.type] || m.type} size="small" variant="outlined" /></TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary" sx={{
                maxWidth: 200,
                display: "block",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap"
              }}>
                    {m.details || "—"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip label={m.isEnabled ? "Enabled" : "Disabled"} size="small" color={m.isEnabled ? "success" : "default"} />
                </TableCell>
                <TableCell align="right">
                  <Box sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 0.5
              }}>
                    <IconButton size="small" onClick={() => openEdit(m)}><Edit2 size={15} /></IconButton>
                    <IconButton size="small" color="error" onClick={() => remove(m._id)}><Trash2 size={15} /></IconButton>
                  </Box>
                </TableCell>
              </TableRow>)}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? t("Edit Payment Method") : t("Add Payment Method")}</DialogTitle>
        <DialogContent sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        pt: 2,
        overflowX: "hidden"
      }}>
          <TextField label={t("Name")} value={form.name} onChange={e => setForm({
          ...form,
          name: e.target.value
        })} fullWidth placeholder={t("Bank Transfer")} />
          <FormControl fullWidth>
            <InputLabel>{t("Type")}</InputLabel>
            <Select value={form.type} onChange={e => setForm({
            ...form,
            type: e.target.value
          })} label={t("Type")}>
              <MenuItem value="cash">{t("Cash on Delivery")}</MenuItem>
              <MenuItem value="bank_transfer">{t("Bank Transfer")}</MenuItem>
              <MenuItem value="custom">{t("Custom")}</MenuItem>
            </Select>
          </FormControl>
          <TextField label={t("Details (shown to customer)")} value={form.details} onChange={e => setForm({
          ...form,
          details: e.target.value
        })} fullWidth multiline rows={3} placeholder={t("IBAN: SA0000000000000000\\nBank: Al Rajhi Bank")} />
          
          <FormControlLabel control={<Switch checked={form.isEnabled} onChange={e => setForm({
          ...form,
          isEnabled: e.target.checked
        })} />} label={t("Enabled")} />
          
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{t("Cancel")}</Button>
          <Button variant="contained" onClick={save} disabled={saving || !form.name.trim()}>
            {saving ? <CircularProgress size={18} /> : t("Save")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>;
};
export default PaymentMethodsTab;
